import React, { useState, useEffect } from 'react';
import { loanBanks } from '../mock';
import { listNewLaunches, listCities, listDevelopers, listInspectionPlans } from '../lib/api';
import { ChevronLeft, ChevronRight, MapPin, Check, ShieldCheck, Ruler, FileText, Star, Home, PhoneCall, Mail, MapPin as Pin, Facebook, Instagram, Youtube, Twitter, Linkedin } from 'lucide-react';
import { Button } from './ui/button';
import { Skeleton } from './ui/skeleton';
import { Link } from 'react-router-dom';
import ServiceBookingModal from './ServiceBookingModal';

export const NewLaunchSection = () => {
  const [start, setStart] = useState(0);
  const perView = 4;
  const [newLaunches, setNewLaunches] = useState([]);
  const [loading, setLoading] = useState(true);
  const total = newLaunches.length;

  useEffect(() => {
    listNewLaunches().then((items) => setNewLaunches(items || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <section className="py-14 md:py-20 bg-white">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <span className="text-xs font-bold uppercase tracking-widest text-[#9a1b2f]">Just Launched</span>
          <h2 className="text-2xl md:text-4xl font-black text-gray-900 tracking-tight mt-1">NEW LAUNCH</h2>
          <div className="w-16 h-1 bg-[#9a1b2f] mx-auto mt-3 rounded-full" />
        </div>

        <div className="relative">
          <div className="mobile-section-scroll md:overflow-hidden">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                    <Skeleton className="aspect-[4/3] w-full" />
                    <div className="p-4 space-y-2"><Skeleton className="h-4 w-2/3" /><Skeleton className="h-3 w-1/2" /></div>
                  </div>
                ))}
              </div>
            ) : (
            <div
              className="mobile-section-track grid grid-flow-col auto-cols-[80%] sm:auto-cols-[45%] lg:auto-cols-[24%] gap-4 transition-transform duration-500"
              style={{ transform: `translateX(calc(-${start} * (100% / ${perView} + 1rem / ${perView})))` }}
            >
              {newLaunches.map((l) => (
                <div key={l.id} className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                  <div className="relative aspect-[4/3] bg-gray-100 overflow-hidden">
                    <img src={l.img} alt={l.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute top-3 left-3 w-11 h-11 rounded-full bg-white shadow-md flex items-center justify-center p-1.5">
                      <img src={l.logo} alt="builder" className="w-full h-full object-contain" />
                    </div>
                    <div className="absolute top-3 right-3">
                      <span className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider bg-[#9a1b2f] text-white rounded-full shadow-md">New</span>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-gray-900 leading-tight mb-1 line-clamp-1 group-hover:text-[#9a1b2f] transition-colors">{l.name}</h3>
                    <div className="flex items-center gap-1 text-xs text-gray-500 mb-3">
                      <MapPin className="w-3 h-3" />{l.location}
                    </div>
                    <div className="flex items-end justify-between">
                      <div>
                        <div className="text-[10px] uppercase font-semibold text-gray-400 tracking-wide">Starts At</div>
                        <div className="text-lg font-black text-[#9a1b2f]">₹ {l.price}</div>
                      </div>
                      <Button size="sm" className="h-8 text-xs bg-[#25D366] hover:bg-[#20b955] text-white">WhatsApp</Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            )}
          </div>
          <button
            onClick={() => setStart((s) => Math.max(0, s - 1))}
            disabled={start === 0}
            className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-lg border border-gray-100 items-center justify-center hover:bg-[#9a1b2f] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => setStart((s) => Math.min(total - perView, s + 1))}
            disabled={start >= total - perView}
            className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-lg border border-gray-100 items-center justify-center hover:bg-[#9a1b2f] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </section>
  );
};

export const HomeInspectionSection = () => {
  const [inspectionPlans, setInspectionPlans] = useState([]);
  const [bookOpen, setBookOpen] = useState(false);
  useEffect(() => { listInspectionPlans().then(setInspectionPlans).catch(() => {}); }, []);
  return (
  <section id="inspection" className="py-14 md:py-20 bg-gradient-to-b from-[#fef2f4] to-white">
    <div className="max-w-[1400px] mx-auto px-4 md:px-6">
      <div className="text-center mb-10">
        <span className="text-xs font-bold uppercase tracking-widest text-[#9a1b2f]">Peace of Mind</span>
        <h2 className="text-2xl md:text-4xl font-black text-gray-900 tracking-tight mt-1">HOME INSPECTION</h2>
        <div className="w-16 h-1 bg-[#9a1b2f] mx-auto mt-3 rounded-full" />
        <div className="flex justify-center gap-3 mt-6 flex-wrap">
          <Button onClick={() => setBookOpen(true)} className="bg-[#9a1b2f] hover:bg-[#7a1524] rounded-full px-6">Book Inspection</Button>
          <Button variant="outline" onClick={() => setBookOpen(true)} className="rounded-full px-6 border-[#9a1b2f] text-[#9a1b2f] hover:bg-[#9a1b2f] hover:text-white">View All Plans</Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-10 items-center mb-10">
        <div>
          <p className="text-gray-600 text-base leading-relaxed mb-6">
            Our expert Home Inspection service helps you identify and resolve issues before you take possession of your new property.
          </p>
          <div className="space-y-4">
            {[
              { icon: ShieldCheck, title: 'Professional Assessment', desc: 'Conducted by qualified experts with years of experience in property inspection' },
              { icon: Ruler, title: 'Detailed Measurement', desc: 'Inch-by-inch carpet area measurement with BBA floor plan comparison' },
              { icon: FileText, title: 'Comprehensive Report', desc: 'Detailed documentation of all findings with recommendations for resolution' },
            ].map((f) => (
              <div key={f.title} className="flex gap-4 p-4 rounded-xl bg-white border border-gray-100 hover:border-[#9a1b2f] transition-colors">
                <div className="w-10 h-10 rounded-lg bg-[#fef2f4] flex items-center justify-center shrink-0">
                  <f.icon className="w-5 h-5 text-[#9a1b2f]" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900">{f.title}</h4>
                  <p className="text-sm text-gray-600 mt-0.5">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative">
          <div className="absolute -inset-4 bg-[#9a1b2f]/5 rounded-3xl blur-2xl" />
          <img
            src="https://housypoint.com/static/images/PDI_landing.png"
            alt="Home Inspection"
            className="relative w-full max-w-md mx-auto"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {inspectionPlans.map((plan) => (
          <div
            key={plan.id}
            className={`relative rounded-2xl p-6 border transition-all hover:-translate-y-1 hover:shadow-xl ${
              plan.highlighted
                ? 'border-[#9a1b2f] bg-white shadow-lg'
                : 'border-gray-100 bg-white'
            }`}
          >
            {plan.highlighted && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1 px-3 py-1 rounded-full bg-[#9a1b2f] text-white text-[10px] font-bold uppercase tracking-wider shadow-md">
                <Star className="w-3 h-3 fill-white" /> Most Popular
              </div>
            )}
            <h3 className="text-xl font-black text-gray-900">{plan.name}</h3>
            <div className="flex items-baseline gap-2 mt-2">
              <span className="text-3xl font-black text-[#9a1b2f]">₹{Number(plan.price || 0).toLocaleString('en-IN')}</span>
              <span className="text-sm text-gray-400 line-through">₹{Number(plan.original_price ?? plan.originalPrice ?? 0).toLocaleString('en-IN')}</span>
            </div>
            <ul className="mt-5 space-y-2">
              {plan.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-gray-700">
                  <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Button className={`w-full mt-6 rounded-full ${plan.highlighted ? 'bg-[#9a1b2f] hover:bg-[#7a1524]' : 'bg-gray-900 hover:bg-gray-800'} text-white`} onClick={() => setBookOpen(true)}>
              Book {plan.name}
            </Button>
          </div>
        ))}
      </div>
    </div>
    <ServiceBookingModal open={bookOpen} onOpenChange={setBookOpen} service="home_inspection" />
  </section>
  );
};

export const LoanSection = () => {
  const [bookOpen, setBookOpen] = useState(false);
  return (
  <section className="py-14 md:py-20 bg-white">
    <div className="max-w-[1400px] mx-auto px-4 md:px-6">
      <div className="text-center mb-8">
        <span className="text-xs font-bold uppercase tracking-widest text-[#9a1b2f]">Financial Partners</span>
        <h2 className="text-2xl md:text-4xl font-black text-gray-900 tracking-tight mt-1">HOME LOAN</h2>
        <div className="w-16 h-1 bg-[#9a1b2f] mx-auto mt-3 rounded-full" />
        <p className="text-gray-600 text-sm mt-4 max-w-2xl mx-auto">Get the best home loan rates from India's top banks with expert guidance throughout the process.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        {loanBanks.map((b, i) => (
          <div key={i} className="group aspect-[4/3] bg-white rounded-2xl border border-gray-100 flex items-center justify-center p-6 hover:shadow-lg hover:border-[#9a1b2f] transition-all cursor-pointer">
            <img src={b} alt="bank" className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform" />
          </div>
        ))}
      </div>
      <div className="flex justify-center mt-8">
        <Button onClick={() => setBookOpen(true)} className="bg-[#9a1b2f] hover:bg-[#7a1524] rounded-full px-8 h-11 font-semibold">Apply for Home Loan</Button>
      </div>
    </div>
    <ServiceBookingModal open={bookOpen} onOpenChange={setBookOpen} service="home_loan" />
  </section>
  );
};

export const PopularCitiesSection = () => {
  const [popularCities, setPopularCities] = useState([]);
  useEffect(() => { listCities().then(setPopularCities).catch(() => {}); }, []);
  return (
  <section className="py-14 md:py-20 bg-gradient-to-b from-gray-50 to-white">
    <div className="max-w-[1400px] mx-auto px-4 md:px-6">
      <div className="text-center mb-10">
        <h2 className="text-2xl md:text-4xl font-black text-gray-900 tracking-tight">POPULAR CITIES</h2>
        <div className="w-16 h-1 bg-[#9a1b2f] mx-auto mt-3 rounded-full" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {popularCities.map((c) => (
          <Link key={c.name} to={`/listings?type=resale&city=${encodeURIComponent(c.name)}`} className="group relative rounded-2xl overflow-hidden aspect-[4/5] shadow-md hover:shadow-2xl transition-all hover:-translate-y-1">
            <img src={c.img} alt={c.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
              <h3 className="text-xl font-black">{c.name}</h3>
              <p className="text-[10px] font-bold uppercase tracking-widest opacity-80">{c.state}</p>
              <div className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/20 backdrop-blur-sm text-xs font-semibold">
                <Home className="w-3 h-3" /> {c.count} Properties
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  </section>
  );
};

export const TrustedDevelopersSection = () => {
  const [trustedDevelopers, setTrustedDevelopers] = useState([]);
  useEffect(() => { listDevelopers().then(setTrustedDevelopers).catch(() => {}); }, []);
  return (
  <section className="py-14 md:py-20 bg-white overflow-hidden">
    <div className="max-w-[1400px] mx-auto px-4 md:px-6">
      <div className="text-center mb-10">
        <h2 className="text-2xl md:text-4xl font-black text-gray-900 tracking-tight">TRUSTED DEVELOPERS</h2>
        <div className="w-16 h-1 bg-[#9a1b2f] mx-auto mt-3 rounded-full" />
      </div>
    </div>
    <div className="relative">
      <div className="marquee-container">
        <div className="marquee-track">
          {[...trustedDevelopers, ...trustedDevelopers].map((d, i) => (
            <div key={i} className="marquee-item">
              <div className="w-28 h-28 md:w-32 md:h-32 bg-white rounded-2xl border border-gray-100 flex items-center justify-center p-4 hover:shadow-lg hover:border-[#9a1b2f] transition-all">
                <img src={d.logo} alt={d.name} className="max-w-full max-h-full object-contain grayscale hover:grayscale-0 transition-all" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
  );
};

export const Footer = () => (
  <footer className="bg-gray-950 text-gray-300">
    <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-14">
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-lg bg-[#9a1b2f] flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <span className="text-xl font-bold text-white">SafeHouse</span>
          </div>
          <p className="text-sm text-gray-400 leading-relaxed">One stop solution for all your real estate needs — buy, sell, rent, home inspection & loans.</p>
          <div className="flex gap-3 mt-5">
            {[Facebook, Instagram, Youtube, Twitter, Linkedin].map((Icon, i) => (
              <a key={i} href="#" className="w-9 h-9 rounded-full bg-white/5 hover:bg-[#9a1b2f] flex items-center justify-center transition-colors">
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>
        <div>
          <h4 className="text-white font-bold mb-4 text-sm uppercase tracking-wider">Listings</h4>
          <ul className="space-y-2 text-sm">
            {[
              { l: 'Resale Properties', to: '/listings?type=resale' },
              { l: 'Rent Properties', to: '/listings?type=rent' },
              { l: 'Resale Commercial', to: '/listings?type=resale_commercial' },
              { l: 'Rent Commercial', to: '/listings?type=rent_commercial' },
            ].map((x) => (
              <li key={x.l}><Link to={x.to} className="hover:text-[#e08b96] transition-colors">{x.l}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-white font-bold mb-4 text-sm uppercase tracking-wider">I want to</h4>
          <ul className="space-y-2 text-sm">
            {['Buy Property', 'Sell Property', 'Rent Property', 'Post Property'].map((l) => (
              <li key={l}><a href="#" className="hover:text-[#e08b96] transition-colors">{l}</a></li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-white font-bold mb-4 text-sm uppercase tracking-wider">Our Expertise</h4>
          <ul className="space-y-2 text-sm mb-4">
            {['Home Inspection', 'Legal Advice', 'Taxation Advice', 'Home Loan'].map((l) => (
              <li key={l}><a href="#" className="hover:text-[#e08b96] transition-colors">{l}</a></li>
            ))}
          </ul>
          <div className="space-y-2 text-xs text-gray-400">
            <div className="flex items-center gap-2"><PhoneCall className="w-3 h-3" /> +91 98100 00000</div>
            <div className="flex items-center gap-2"><Mail className="w-3 h-3" /> hello@safehouse.com</div>
            <div className="flex items-start gap-2"><Pin className="w-3 h-3 mt-0.5" /> Gurgaon, Haryana, India</div>
          </div>
        </div>
      </div>
      <div className="mt-10 pt-6 border-t border-white/10 text-xs text-gray-500 flex flex-col md:flex-row justify-between gap-3">
        <span>© {new Date().getFullYear()} SafeHouse. All rights reserved.</span>
        <div className="flex gap-4">
          <a href="#" className="hover:text-white">Privacy Policy</a>
          <a href="#" className="hover:text-white">Terms of Service</a>
          <a href="#" className="hover:text-white">Sitemap</a>
        </div>
      </div>
    </div>
  </footer>
);
